import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { GridColumnVisibilityModel } from '@mui/x-data-grid';
import { valueDiffers } from './sampleFilterStoreUtils';
import { createUserScopedJSONStorage, readUserScopedJson } from '@/lib/browser/userScopedStorage';
import { SampleSearchKind, type SampleSearchCriteria } from '@/api/ApiClient';
import { reviveSampleSearchCriteriaDates } from '@/lib/utils/reviveSampleSearchCriteriaDates';

const STORAGE_KEY = 'sampleFilters';
export const COLUMN_VISIBILITY_STORAGE_KEY = 'sampleColumnVisibility';

/** Default filter criteria for sample search. Kind Both = API baseline when Kind is omitted. */
export const defaultFilterCriteria: SampleSearchCriteria = {
  sampleLabel: '',
  projectId: '',
  status: '',
  addedByUserId: '',
  addedByResearchGroup: '',
  grownByUserId: '',
  grownByResearchGroup: '',
  reportingCategory: '',
  addedAfter: undefined,
  addedBefore: undefined,
  grownAfter: undefined,
  grownBefore: undefined,
  includeSplitPieces: false,
  includeFinalized: true,
  techniqueId: '',
  instrumentId: '',
  characterizationTechniqueId: '',
  characterizationInstrumentId: '',
  materials: [],
  shipped: '',
  shippedAfter: undefined,
  shippedBefore: undefined,
  substrateName: '',
  substrateVendor: '',
  substrateLot: '',
  substrateOrientation: '',
  substratePurity: undefined,
  multiSampleGrowthOnly: false,
  /** Empty = all; `substrates` = no growth yet; `grown` = has synthesis activity. */
  specimenType: '',
  kind: SampleSearchKind.Both,
};

/** Flags indicating that a filter was set from the basic row (growth date dropdown or My samples), so we don't show the Filter dot for those. */
export interface FilterSourceFlags {
  /** True when growth date range was set via the basic "Growth date" dropdown (not extended panel). */
  growthDateSetFromBasicRow?: boolean;
  /** True when "Added By" was set via the basic "My samples" checkbox (not extended panel). */
  addedBySetFromBasicRow?: boolean;
  /** Top-level technique IDs. When techniqueId is in this set, don't count it for the dot (basic row); when it's a child technique, count it (extended "Detailed technique"). */
  topLevelTechniqueIds?: string[];
}

/**
 * Returns true if any filter is set that should show the Filter button indicator.
 * Basic-row-only filters do not count: Sample ID, Project, top-level Technique, Growth date dropdown, My samples.
 * Extended filters count: Live/snapshot, Detailed technique (child), Growth dates from panel, Added By from panel, and all other criteria.
 */
export function hasExtendedFiltersSet(filters: SampleSearchCriteria, sourceFlags?: FilterSourceFlags | null): boolean {
  const def = defaultFilterCriteria;
  const keys = Object.keys(def) as (keyof SampleSearchCriteria)[];

  for (const key of keys) {
    const a = filters[key];
    const b = def[key];
    if (!valueDiffers(a, b)) continue;

    if (key === 'sampleLabel' || key === 'projectId') continue;
    // Technique: only count for the dot when it's a detailed (child) technique from the extended panel.
    // Top-level technique from the basic row does not show the dot. If we don't have the list yet, don't count.
    if (key === 'techniqueId') {
      const topIds = sourceFlags?.topLevelTechniqueIds;
      if (!topIds?.length) continue; // No list: can't tell, don't count (avoids dot while options load).
      if (typeof a === 'string' && topIds.includes(a)) continue; // Top-level: basic row, no dot.
      return true; // Child technique or unknown id: extended filter, show dot.
    }
    if (key === 'grownAfter' || key === 'grownBefore') {
      if (sourceFlags?.growthDateSetFromBasicRow) continue;
      return true;
    }
    if (key === 'addedByUserId') {
      if (sourceFlags?.addedBySetFromBasicRow) continue;
      return true;
    }
    return true;
  }
  return false;
}

export const defaultColumnVisibilityModel: GridColumnVisibilityModel = {
  projectLabel: true,
  sampleLabel: true,
  creationDate: false,
  substrate: true,
  material: true,
  technique: true,
  lastActivity: true,
  owner: true,
  status: true,
  lastActivityDate: false,
  lastChangedDate: false,
  lastChangedBy: false,
  transferred: false,
  shipped: false,
  faculty: false,
  instrument: false,
  substrateName: false,
  substrateVendor: false,
  substrateLot: false,
  substrateOrientation: false,
  substratePurity: false,
};

export function loadStoredColumnVisibility(): GridColumnVisibilityModel {
  if (typeof window === 'undefined') return defaultColumnVisibilityModel;

  try {
    const stored = readUserScopedJson(COLUMN_VISIBILITY_STORAGE_KEY);
    if (!stored) return defaultColumnVisibilityModel;
    return JSON.parse(stored) as GridColumnVisibilityModel;
  } catch {
    return defaultColumnVisibilityModel;
  }
}

interface FilterStore {
  /** Current filter criteria for sample search */
  filters: SampleSearchCriteria;
  /**
   * Increments when {@link ensureFreshSampleGridServerRequest} runs (e.g. Apply) so the grid’s
   * cache key changes and the next load hits the server even if filters/page/sort are unchanged.
   * Not persisted.
   */
  sampleGridApplyGeneration: number;
  /** True while the sample grid is actively fetching results */
  isSearching: boolean;
  /** True when growth date was set via the basic row dropdown (not extended panel). */
  growthDateSetFromBasicRow: boolean;
  /** True when added-by was set via the basic row "My samples" (not extended panel). */
  addedBySetFromBasicRow: boolean;
  /** Sets search-in-progress state (not persisted) */
  setIsSearching: (isSearching: boolean) => void;
  /** Updates the filter criteria and persists to localStorage. Optionally set which filters came from the basic row. */
  setFilters: (filters: SampleSearchCriteria, options?: FilterSourceFlags) => void;
  /** Resets filters to default criteria and removes from localStorage */
  resetFilters: () => void;
  /** Current pagination state */
  paginationModel: { page: number; pageSize: number };
  /** Updates pagination state, accepts either new state or updater function */
  setPaginationModel: (
    model:
      | { page: number; pageSize: number }
      | ((prev: { page: number; pageSize: number }) => { page: number; pageSize: number })
  ) => void;
  /**
   * Call when the user explicitly applies search (Apply): increments {@link sampleGridApplyGeneration}
   * so the sample grid will not reuse the in-memory snapshot and will refetch from the server.
   */
  ensureFreshSampleGridServerRequest: () => void;
}

/**
 * Zustand store for managing sample filters and pagination
 * Now uses Zustand's persist middleware for automatic localStorage persistence.
 */
export const useSampleFilterStore = create<FilterStore>()(
  persist(
    (set, get) => ({
      filters: defaultFilterCriteria,
      sampleGridApplyGeneration: 0,
      isSearching: false,
      growthDateSetFromBasicRow: false,
      addedBySetFromBasicRow: false,
      setIsSearching: (isSearching) => set({ isSearching }),
      setFilters: (filters, options) =>
        set({
          filters: reviveSampleSearchCriteriaDates(filters),
          growthDateSetFromBasicRow: options?.growthDateSetFromBasicRow ?? false,
          addedBySetFromBasicRow: options?.addedBySetFromBasicRow ?? false,
        }),
      resetFilters: () =>
        set({
          filters: defaultFilterCriteria,
          growthDateSetFromBasicRow: false,
          addedBySetFromBasicRow: false,
        }),
      paginationModel: { page: 0, pageSize: 25 },
      setPaginationModel: (modelOrUpdater) => {
        const current = get().paginationModel;
        const newModel = typeof modelOrUpdater === 'function' ? modelOrUpdater(current) : modelOrUpdater;
        set({ paginationModel: newModel });
      },
      ensureFreshSampleGridServerRequest: () =>
        set((state) => ({ sampleGridApplyGeneration: state.sampleGridApplyGeneration + 1 })),
    }),
    {
      name: STORAGE_KEY,
      storage: createUserScopedJSONStorage(),
      partialize: (state) => ({
        filters: state.filters,
        paginationModel: state.paginationModel,
        growthDateSetFromBasicRow: state.growthDateSetFromBasicRow,
        addedBySetFromBasicRow: state.addedBySetFromBasicRow,
      }),
      merge: (persistedState, currentState) => {
        const p = persistedState as
          | Partial<
              Pick<FilterStore, 'filters' | 'paginationModel' | 'growthDateSetFromBasicRow' | 'addedBySetFromBasicRow'>
            >
          | undefined
          | null;
        const c = currentState as FilterStore;
        if (!p) return c;
        return {
          ...c,
          ...p,
          filters: reviveSampleSearchCriteriaDates({
            ...defaultFilterCriteria,
            ...(p.filters ?? {}),
          }),
          paginationModel: p.paginationModel ?? c.paginationModel,
          growthDateSetFromBasicRow: p.growthDateSetFromBasicRow ?? c.growthDateSetFromBasicRow,
          addedBySetFromBasicRow: p.addedBySetFromBasicRow ?? c.addedBySetFromBasicRow,
        };
      },
    }
  )
);
